import axios from "axios";
import Config from "../config";
import loggerService from "./logger.service";
import { issetAuthToken, getAuthToken, issetActiveWorkSpace, getActiveWorkSpace, issetAuthType, getAuthType } from "utils/storage";

// Request methods
const GET = "GET";
const POST = "POST";
const PUT = "PUT";
const DELETE = "DELETE";

const requestTracker = {};
const allowDuplicateEndpoints = [];

/**
 * Set headers & base url
 */
export function getHttpHeader() {
  // Set default headers
  const headers = {
    "Content-Type": "application/json",
    "Access-Control-Allow-Origin": "*",
    Timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  // if (issetAuthToken()) {
  //   headers['Authorization'] = `Bearer ${getAuthToken()}`;
  //   console.log(issetAuthToken(), getAuthToken())
  // }else{
  //   console.log(issetAuthToken(), getAuthToken())
  // }
  return headers;
}

export const axiosBase = axios.create({
  baseURL: Config.apiBaseUrl,
  headers: getHttpHeader(),
});

// Create an Axios request interceptor
axiosBase.interceptors.request.use(
  (config) => {
    // Get the user's authentication status (you can use a state management library like Redux or React Context)

    // Update the headers based on the user's authentication status
    if (issetAuthToken()) {
      config.headers["Authorization"] = `Bearer ${getAuthToken()}`;
      config.headers["User-From-Frontend"] = 'true';
    } else {
      // Remove the Authorization header if the user is not authenticated
      delete config.headers["Authorization"];
    }

    if (issetActiveWorkSpace()) {
      config.headers["WorkspaceId"] = `${getActiveWorkSpace()}`;
    } else {
      // Remove the Authorization header if the user is not authenticated
      delete config.headers["WorkspaceId"];
    }

    if(issetAuthType()){
      config.headers["UserType"] = `${getAuthType()}`;
    } else {
      delete config.headers["UserType"];
    }

    if (allowDuplicateEndpoints.some((ep) => config.url.includes(ep))) {
      return config;
    }

    const requestKey = `${config.method}-${config.url}-${JSON.stringify(
      config.params
    )}`;

    if (requestTracker[requestKey]) {
      return Promise.resolve(requestTracker[requestKey]);
    }

    // Mark the request as in-progress
    const cancelTokenSource = axios.CancelToken.source();
    config.cancelToken = cancelTokenSource.token;

    // Track the ongoing request
    const requestPromise = axiosBase(config).then(
      (response) => response,
      (error) => Promise.reject(error)
    );

    requestTracker[requestKey] = requestPromise;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response Interceptor to clean up request tracker
axiosBase.interceptors.response.use(
  (response) => {
    const requestKey = `${response.config.method}-${response.config.url}-${JSON.stringify(response.config.params)}`;
    delete requestTracker[requestKey]; // Remove the request from the tracker after completion
    return response;
  },

  async (error) => {
    // Safely clean up the request tracker
    if (error.config) {
      const requestKey = `${error.config.method}-${error.config.url}-${JSON.stringify(error.config.params)}`;
      delete requestTracker[requestKey];
    }

    return Promise.reject(error);
  }
);

/**
 * Http request
 */
export const request = async (
  method,
  path,
  httpParams,
  body,
  disableLoader = false,
  props
) => {
  // Console request time
  consoleRequestResponseTime("request", Config.apiBaseUrl + "" + path);
// console.log('props',props);


  // Check method
  // eslint-disable-next-line default-case
  switch (method) {
    // Get
    case GET:
      return axiosBase
        .get(path, { params: httpParams })
        .then(function (response) {
          // handle success
          const processedData = processResponseData("success", path, response);
          return processedData;
        })
        .catch(function (error) {
          // handle error
          processResponseData("failure", path, error);
          throw error;
        })
        .finally(function () {});

    // Post
    case POST:
      return axiosBase
        .post(path, body, { params: httpParams })
        .then(function (response) {
          // handle success
          const processedData = processResponseData("success", path, response);
          return processedData;
        })
        .catch(function (error) {
          // handle error
          processResponseData("failure", path, error);
          throw error;
        })
        .finally(function () {});

    // Put
    case PUT:
      return axiosBase
        .put(path, body, { params: httpParams })
        .then(function (response) {
          // handle success
          const processedData = processResponseData("success", path, response);
          return processedData;
        })
        .catch(function (error) {
          // handle error
          processResponseData("failure", path, error);
          throw error;
        })
        .finally(function () {});
    // DELETE
    case DELETE:
      return axiosBase
        .delete(path, { data: body, params: httpParams })
        .then(function (response) {
          // handle success
          const processedData = processResponseData("success", path, response);
          return processedData;
        })
        .catch(function (error) {
          // handle error
          processResponseData("failure", path, error);
          throw error;
        })
        .finally(function () {});
  }
};

/**
 * Process the response data
 */
export const processResponseData = (type, path, data, failureMsg) => {
  // If success and data is object
  if (type === "success") {
    // data = convertNulltoEmpty(data);
    if (Config.trackHttpResponseInConsole) {
      loggerService.showLog("Response Success");
      loggerService.showLog(["Request Url", Config.apiBaseUrl + "" + path]);
      loggerService.showLog(["Body", data]);
    }
    return data;
  } else {
    if (Config.trackHttpResponseInConsole) {
      loggerService.showLog("Response Failure");
      loggerService.showLog(["Url", Config.apiBaseUrl + "" + path]);
      loggerService.showLog(["Body", data]);
    }

    /**
     * Show error msg if
     * 1. Message available in service
     * 2. Otherwise show custom error from each service request
     * 3. Otherwise, show default message 'Service Failure'
     */
    // Need to Confirm params
    console.log(failureMsg);
  }
};

/**
 * Convert json null to empty write console
 */
export const convertNulltoEmpty = (data) => {
  let stringifyData = JSON.stringify(data).replace(/null/i, '""');
  stringifyData = stringifyData.replace(/null/g, '""');
  const json = JSON.parse(stringifyData);
  return json;
};

/**
 * Request / Response Time Tracker
 */
const consoleRequestResponseTime = (type, url) => {
  if (Config.trackHttpTimeInConsole) {
    if (type === "request") {
      console.log("Request Url", url);
      console.log("Time Started", new Date());
    } else {
      console.log("Response Url", url);
      console.log("Time Ended", new Date());
    }
  }
};

/**
 * File upload handler
 */
export const doFileUpload = async (url, params) => {
  try {
    const formData = new FormData();
    if (params[0].body?.pageType === "ticket") {
      formData.append(`${params[0].body?.pageType === 'pssTicket' ? 'ticketAttachment': 'files'}`, params[0].file);
      formData.append("ticket_id", params[0].body?.ticketId);
    } else if (params[0].body?.pageType === "tool") {
      formData.append("files", params[0].file);
      formData.append("TicketId", params[0].body?.ticketId);
      formData.append("ToolId", params[0].body?.toolId);
      formData.append("tool_ticket_id", params[0].body?.tool_ticket_id);
    } else if (params[0].body?.pageType === "comment") {
      params[0].file.forEach((val) => {
        formData.append(`attachedFiles`, val);
      });
      formData.append("ticket_id", params[0].body?.ticket_id);
      formData.append("tool_id", params[0].body?.tool_id);
      formData.append("tool_ticket_id", params[0].body?.tool_ticket_id);
      formData.append("comment_id", params[0].body?.comment_id);
      formData.append("content", params[0].body?.content);
      formData.append("mentions", params[0].body?.mentions);

      /** MODULE */
      if(params[0].body?.module_iteration_id){ formData.append("module_iteration_id", params[0].body?.module_iteration_id); }
      if(params[0].body?.main_module_id){ formData.append("main_module_id", params[0].body?.main_module_id); }
      if(params[0].body?.submodule_id){ formData.append("submodule_id", params[0].body?.submodule_id); }

      // formData.append('deleted_attachments', params[0].body?.deleted_attachments );
      const deletedAttachmentIds = params[0].body?.deleted_attachments.join(
        ","
      );
      formData.append("deleted_attachments", deletedAttachmentIds);
    }else if (params[0].body?.pageType === "pss") { // UPLOAD HAPPENS FROM TICKET EDIT FORM
      formData.append("ticket_id", params[0].body?.ticket_id);
      // formData.append("moduleAttachment", params[0].moduleAttachment);
      // formData.append("ticketAttachment", params[0].ticketAttachment);

      params[0].moduleAttachment.forEach((attachment, index) => {
        attachment.attachmentFiles.forEach((file, fileIndex) => {
          formData.append(
            `moduleAttachment`,
            file,
            file.name // Include moduleId in the file name for backend identification
          );
        });
      });

      params[0].submoduleAttachment.forEach((attachment, index) => {
        attachment.attachmentFiles.forEach((file, fileIndex) => {
          formData.append(
            `submoduleAttachment`,
            file,
            file.name
          );
        });
      });
      
      // Append ticketAttachment files (if there are files in this array)
      params[0].ticketAttachment.forEach((file, index) => {
        formData.append(`ticketAttachment`, file);
      });

    } else if (params[0].body?.pageType === "pssTicket") { // UPLOAD HAPPENS FROM TICKET DETAIL PAGE
      formData.append("ticket_id", params[0].body?.ticketId);
      params[0].file.forEach((file, index) => {
        formData.append(`ticketAttachment`, file);
      });
    }

    // if (params[0]) {
    //   formData.append('data', JSON.stringify(params[0]));
    // }
    const createXHR = () => new XMLHttpRequest();
    const response = await axios.post(url, formData, {
      baseURL: Config.apiBaseUrl,
      headers: {
        Accept: "multipart/form-data",
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${getAuthToken()}`, // Include your auth token if needed
        WorkspaceId: `${getActiveWorkSpace()}`,
        UserType: issetAuthType() ? `${getAuthType()}` : "",
        "User-From-Frontend": true
      },
      httpAgent: createXHR,
    });

    const validResponse = response.data;
    const processedData = processResponseData("success", url, validResponse);
    return processedData;
  } catch (error) {
    console.error("Error during file upload:", error);
    // processResponseData('failure', url, error.response.data, failureMsg);
    throw error;
  }
};

/**
 * File Download
 */
export const doFileDownload = async (
  path,
  httpParams,
  body,
  disableLoader = false
) => {
  // Console request time
  consoleRequestResponseTime("request", Config.apiBaseUrl + "" + path);

  try {
    return axiosBase
      .post(path, body, { params: httpParams, responseType: "blob" })
      .then((res) => {
        if (res?.data) {
          const blobData = new Blob([res?.data]);
          const blobUrl = URL.createObjectURL(blobData);
          var a = document.createElement("a");
          a.href = blobUrl;
          a.download = body.file_name;
          a.click(); //Downloaded file

          // Revoke the Blob URL to free up memory
          URL.revokeObjectURL(blobUrl);
        }
      })
      .catch(function (error) {
        // handle error
        processResponseData("failure", path, error);
        throw error;
      })
      .finally(function () {});
  } catch (error) {
    console.error("Error during file upload:", error);
    // processResponseData('failure', url, error.response.data, failureMsg);
    throw error;
  }
};

export default {
  GET: (path, ...props) => request(GET, path, ...props),
  POST: (path, ...props) => request(POST, path, props.params, ...props),
  PUT: (path, ...props) => request(PUT, path, props.params, ...props),
  DELETE: (path, ...props) => request(DELETE, path, props.params, ...props),
  FILEUPLOAD: (path, ...props) => doFileUpload(path, props),
  FILEDOWNLOAD: (path, ...props) =>
    doFileDownload(path, props.params, ...props),
};
